import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components'
import {
  EXPORT_DRIVER_OVERTIME,
  EXPORT_DRIVER_WORK_HOURS,
} from '@/modules/exports/mutations'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { DownloadIcon } from 'lucide-react'
import { useDriverWorkFilter } from './driver-work-filter'

export type DriverWorkTab = 'hours' | 'overtime'

/** Exports the open tab with the filters it is showing. */
export const DriverWorkExport: React.FC<{ tab: DriverWorkTab }> = ({ tab }) => {
  const translations = useTranslate({
    export: 'common.export',
    excel: 'common.excel',
    pdf: 'common.pdf',
    exportStarted: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    error: 'error.generic-server-error',
  })
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()
  const { filter } = useDriverWorkFilter()

  const options = {
    onCompleted(
      data:
        | { ok?: boolean | null; exportTimeout?: boolean | null }
        | null
        | undefined
    ) {
      if (data?.exportTimeout) addToast('info', translations.exportTimeout)
      else if (data?.ok) addToast('success', translations.exportStarted)
      else addToast('error', translations.error)
    },
    onError: onErrorAuto(translations.error),
  }
  const [exportHours, hours] = useMutation(EXPORT_DRIVER_WORK_HOURS, {
    ...options,
    onCompleted: data => options.onCompleted(data.exportDriverWorkHours),
  })
  const [exportOvertime, overtime] = useMutation(EXPORT_DRIVER_OVERTIME, {
    ...options,
    onCompleted: data => options.onCompleted(data.exportDriverOvertime),
  })

  const runExport = (exportFormat: 'EXCEL' | 'PDF') => {
    const variables = {
      filters: {
        year: filter.year,
        drivers: filter.drivers,
        ownedVehiclesOnly: filter.ownedVehiclesOnly,
      },
      exportFormat,
    }
    void (tab === 'overtime'
      ? exportOvertime({ variables })
      : exportHours({ variables }))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={hours.loading || overtime.loading}
        >
          <DownloadIcon />
          {translations.export}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => runExport('EXCEL')}>
          {translations.excel}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => runExport('PDF')}>
          {translations.pdf}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
